import type ControlBase from '../fabric/ControlBase';

// The standard draggable control handle.
function renderDragControl(
  ctx: CanvasRenderingContext2D,
  left: number,
  top: number
) {
  const size = 10;

  ctx.save();
  ctx.fillStyle = 'white';
  ctx.strokeStyle = '#999999';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(left, top, size / 2, 0, 2 * Math.PI, false);
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

// Control for spliting connector lines.
function renderSplitControl(
  ctx: CanvasRenderingContext2D,
  left: number,
  top: number
) {
  const size = 10;

  ctx.save();
  ctx.globalAlpha = 0.5;
  ctx.fillStyle = '#5ca5e0';
  ctx.strokeStyle = 'white';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(left, top, size / 2, 0, 2 * Math.PI, false);
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

// Control for creating or linking connectors.
function renderConnectControl(
  ctx: CanvasRenderingContext2D,
  left: number,
  top: number,
  zoom: number,
  hovered: boolean = false
) {
  let size: number;
  ctx.save();

  if (hovered) {
    size = 10;
    ctx.fillStyle = '#0073cf';
  } else {
    size = 8;
    ctx.fillStyle = '#dbefff';
  }

  ctx.lineWidth = 1.5 / zoom;
  ctx.strokeStyle = '#0073cf';
  ctx.beginPath();
  ctx.arc(left, top, size / 2 / zoom, 0, 2 * Math.PI, false);
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

function renderAddControl(
  ctx: CanvasRenderingContext2D,
  left: number,
  top: number,
  hovered: boolean,
  size: number
) {
  ctx.save();
  ctx.lineJoin = 'round';
  ctx.fillStyle = 'white';
  ctx.shadowColor = 'rgba(0, 0, 0, 0.08)';
  ctx.shadowBlur = 8;
  const radius = 3;
  left = left - size / 2;
  top = top - size / 2;

  ctx.beginPath();
  ctx.moveTo(left + radius, top);
  // Draw the top line of the rectangle
  ctx.lineTo(left + size - radius, top);
  // Draw the top-right corner of the rectangle
  ctx.arcTo(left + size, top, left + size, top + radius, radius);
  // Draw the right side of the rectangle
  ctx.lineTo(left + size, top + size - radius);
  // Draw the bottom-right corner of the rectangle
  ctx.arcTo(left + size, top + size, left + size - radius, top + size, radius);
  // Draw the bottom side of the rectangle
  ctx.lineTo(left + radius, top + size);
  // Draw the bottom-left corner of the rectangle
  ctx.arcTo(left, top + size, left, top + size - radius, radius);
  // Draw the left side of the rectangle
  ctx.lineTo(left, top + radius);
  // Draw the top-left corner of the rectangle
  ctx.arcTo(left, top, left + radius, top, radius);
  ctx.fill();

  ctx.lineWidth = 2;
  ctx.strokeStyle = hovered ? '#0073CF' : '#666666';
  ctx.lineCap = 'round';
  const offset = 5;
  // Draw the vertical line of the plus
  ctx.beginPath();
  ctx.moveTo(left + size / 2, top + offset);
  ctx.lineTo(left + size / 2, top + size - offset);
  ctx.stroke();

  // Draw the horizontal line of the plus
  ctx.beginPath();
  ctx.moveTo(left + offset, top + size / 2);
  ctx.lineTo(left + size - offset, top + size / 2);
  ctx.stroke();
  ctx.restore();
}

function renderOrderControl(
  control: ControlBase,
  ctx: CanvasRenderingContext2D,
  left: number,
  top: number,
  fabricObject: fabric.Object,
  width: number,
  height: number,
  horizontal = true
) {
  if (control.isActive(fabricObject)) {
    fabricObject?.canvas?.setCursor('grabbing');
  }
  ctx.save();
  ctx.lineJoin = 'round';
  ctx.fillStyle = control.isActive(fabricObject) ? '#0073CF' : '#FFF';
  ctx.shadowColor = 'rgba(0, 0, 0, 0.08)';
  ctx.shadowBlur = 8;
  ctx.strokeStyle = '#E1E1E1';
  ctx.lineWidth = 1;

  const radius = 3;
  left = left - width / 2;
  top = top - height / 2;

  ctx.beginPath();
  ctx.moveTo(left + radius, top);
  // Draw the top line of the rectangle
  ctx.lineTo(left + width - radius, top);
  // Draw the top-right corner of the rectangle
  ctx.arcTo(left + width, top, left + width, top + radius, radius);
  // Draw the right side of the rectangle
  ctx.lineTo(left + width, top + height - radius);
  // Draw the bottom-right corner of the rectangle
  ctx.arcTo(
    left + width,
    top + height,
    left + width - radius,
    top + height,
    radius
  );
  // Draw the bottom side of the rectangle
  ctx.lineTo(left + radius, top + height);
  // Draw the bottom-left corner of the rectangle
  ctx.arcTo(left, top + height, left, top + height - radius, radius);
  // Draw the left side of the rectangle
  ctx.lineTo(left, top + radius);
  // Draw the top-left corner of the rectangle
  ctx.arcTo(left, top, left + radius, top, radius);
  ctx.stroke();
  ctx.fill();

  // Calculate the dot positions for a vertical vs horizontal control
  const dotRadius = 1.2;
  const dotPadding = 2.4;
  const rowCount = horizontal ? 3 : 2;
  const colCount = horizontal ? 2 : 3;
  const xOffset = horizontal
    ? (width * 2) / 5 - dotRadius
    : (width - dotRadius * 2 * 3 - dotPadding * 2) / 2;
  const yOffset = horizontal
    ? (height - dotRadius * 2 * 3 - dotPadding * 2) / 2
    : (height * 2) / 5 - dotRadius;

  const startX = left + xOffset;
  const startY = top + yOffset;

  for (let i = 0; i < rowCount; i++) {
    for (let j = 0; j < colCount; j++) {
      const rowPadding = horizontal
        ? j * (width / 3)
        : dotRadius + j * (dotRadius * 2 + dotPadding);
      const colPadding = horizontal
        ? dotRadius + i * (dotRadius * 2 + dotPadding)
        : i * (height / 3);

      const dotX = startX + rowPadding;
      const dotY = startY + colPadding;
      ctx.beginPath();
      ctx.arc(dotX, dotY, dotRadius, 0, 2 * Math.PI);
      if (control.isActive(fabricObject)) {
        ctx.fillStyle = '#FFF';
      } else if (control.hovered) {
        ctx.fillStyle = '#0073CF';
      } else {
        ctx.fillStyle = '#666';
      }
      ctx.fill();
    }
  }
  ctx.restore();
}

export {
  renderDragControl,
  renderSplitControl,
  renderConnectControl,
  renderAddControl,
  renderOrderControl,
};
